import { useRef, useState } from "react";
import { upload } from "@vercel/blob/client";
import { api } from "../api";
import { useApi } from "../hooks";

type StorageStatus = {
  configured: boolean;
  max_size: number;
  content_types: string[];
};
export function AssetUpload({
  clientId,
  onDone,
}: {
  clientId: number;
  onDone: () => void;
}) {
  const storage = useApi<StorageStatus>("/api/asset-files/status");
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [notice, setNotice] = useState("");
  const [pendingId, setPendingId] = useState<string | null>(null);
  async function confirm(id: string) {
    await api(`/api/asset-files/${id}/complete`, { method: "POST" });
    setPendingId(null);
    setFile(null);
    if (input.current) input.current.value = "";
    setNotice("File stored privately in NOLA.");
    onDone();
  }
  async function save() {
    if (!file && !pendingId) return;
    setBusy(true);
    setNotice("");
    try {
      if (pendingId) {
        await confirm(pendingId);
        return;
      }
      if (!file || !storage.data) return;
      if (
        file.size > storage.data.max_size ||
        !storage.data.content_types.includes(file.type)
      )
        throw Error("Choose an image, video, audio, PDF, text or Office file of 100 MB or less.");
      const assetType = file.type.startsWith("image/")
        ? "image"
        : file.type.startsWith("video/")
          ? "video"
          : "document";
      const prepared = await api<{ id: string; pathname: string }>(
        "/api/asset-files/prepare",
        {
          method: "POST",
          body: JSON.stringify({
            client_id: clientId,
            filename: file.name,
            content_type: file.type,
            size_bytes: file.size,
            asset_type: assetType,
          }),
        },
      );
      await upload(prepared.pathname, file, {
        access: "private",
        handleUploadUrl: "/api/asset-files/upload",
        clientPayload: prepared.id,
        headers: { "X-NL-AOS-Request": "1" },
        multipart: file.size > 4 * 1024 * 1024,
        onUploadProgress: (e) => setProgress(Math.round(e.percentage)),
      });
      setPendingId(prepared.id);
      await confirm(prepared.id);
    } catch (e) {
      setNotice(
        e instanceof Error ? e.message : "Upload failed. Please retry.",
      );
    } finally {
      setBusy(false);
    }
  }
  if (storage.error)
    return (
      <p role="alert">
        Private storage could not be checked.{" "}
        <button className="text-button" onClick={storage.reload}>
          Retry
        </button>
      </p>
    );
  return (
    <details className="panel asset-upload">
      {/* Named for where the file goes and its limit, so it reads apart from "Upload to Drive" (4 MB) and "New asset link". */}
      <summary><span>Store a file privately in NOLA</span><small>Up to 100 MB</small></summary>
      <p className="muted">
        Images, video, audio, PDF, text and Office documents up to 100 MB.
        Only people with access to this client can download them.
      </p>
      {storage.data && !storage.data.configured ? (
        <p>
          Private file storage isn't set up yet. Asset links still work.
        </p>
      ) : (
        <div className="toolbar">
          <input
            ref={input}
            type="file"
            aria-label="Private asset file"
            accept={storage.data?.content_types.join(",")}
            disabled={busy || Boolean(pendingId)}
            onChange={(e) => {
              setFile(e.target.files?.[0] || null);
              setNotice("");
              setProgress(0);
            }}
          />
          <button
            className="button secondary"
            disabled={busy || (!file && !pendingId) || !storage.data}
            onClick={() => void save()}
          >
            {busy
              ? `Uploading ${progress}%`
              : pendingId
                ? "Retry confirmation"
                : "Upload file"}
          </button>
        </div>
      )}
      {notice && <p role="status">{notice}</p>}
    </details>
  );
}
